import { Component, Input, ElementRef, ViewChild, OnInit, Optional, Self, inject } from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@dental-ui/shared/i18n';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { EMAIL_PATTERN, PHONE_PATTERN } from '@dental-ui/shared/consts';

@Component({
  selector: 'lib-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
})
export class InputComponent implements ControlValueAccessor, OnInit {
  private translateService = inject(TranslateService);
  @Input() label = '';
  @Input() placeholder = '';
  @Input() type = 'text';
  @Input() disabled = false;
  @Input() required = false;
  @Input() errorMessage = '';
  @Input() hint = '';
  @Input() icon = '';
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() maxLength?: number;

  @ViewChild('inputElement', { static: false }) inputElement?: ElementRef<HTMLInputElement>;

  inputId = `input-${Math.random().toString(36).substring(2, 9)}`;

  value = '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: string) => {
    // ControlValueAccessor interface requirement
  };
  onTouched = () => {
    // ControlValueAccessor interface requirement
  };

  // ControlValueAccessor pattern requires NgControl injection via constructor
  // This is the standard Angular pattern for custom form controls
  // eslint-disable-next-line
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (this.ngControl && this.type === 'email') {
      const control = this.ngControl.control;
      if (control) {
        const existingValidators = control.validator;
        control.setValidators([
          existingValidators ? existingValidators : Validators.nullValidator,
          Validators.pattern(EMAIL_PATTERN)
        ]);
        control.updateValueAndValidity();
      }
    }

    if (this.ngControl && this.type === 'tel') {
      const control = this.ngControl.control;
      if (control) {
        const existingValidators = control.validator;
        control.setValidators([
          existingValidators ? existingValidators : Validators.nullValidator,
          Validators.pattern(PHONE_PATTERN)
        ]);
        control.updateValueAndValidity();
      }
    }
  }

  get hasError(): boolean {
    if (this.errorMessage) {
      return true;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      return true;
    }
    return false;
  }

  get errorText(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (this.ngControl && this.ngControl.errors && this.ngControl.touched) {
      const errors = this.ngControl.errors;
      if (errors['required']) {
        return 'This field is required';
      }
      if (errors['email'] || (this.type === 'email' && errors['pattern'])) {
        return this.translateService.getText('MESSAGES.INVALID_EMAIL_FORMAT') || 'Please enter a valid email';
      }
      if (this.type === 'tel' && errors['pattern']) {
        return this.translateService.getText('MESSAGES.INVALID_PHONE_FORMAT') || 'Please enter a valid phone number';
      }
      if (errors['min']) {
        return `Minimum value is ${errors['min'].min}`;
      }
      if (errors['max']) {
        return `Maximum value is ${errors['max'].max}`;
      }
      if (errors['minlength']) {
        return `Minimum length is ${errors['minlength'].requiredLength}`;
      }
      if (errors['maxlength']) {
        return `Maximum length is ${errors['maxlength'].requiredLength}`;
      }
      return 'Invalid value';
    }
    return '';
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
  }

  onBlur(): void {
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    if (this.inputElement) {
      this.inputElement.nativeElement.value = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

